import React, {
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
} from "react";
import { useAuth } from "../../customHooks/useAuth";
import { KeenAPI } from "../../API/KeenAPI";
import { Chart } from "../../components/Chart";
import { Box, CircularProgress, Typography } from "@mui/material";

type Props = {
  type: string[];
};

export default function TileUsage({ type }: Props) {
  const auth = useAuth();
  const keenIOAPI = useMemo(
    () => new KeenAPI(auth.user.additionalCookies),
    [auth.user.additionalCookies]
  );
  const [series, setSeries] = useState<any>(null);
  const [dailyUsers, setDailyUsers] = useState<any>(null);
  const chartRef = useRef<any>(null);
  const containerRef = useRef<any>(null);

  const _dataPrepare = useCallback(
    (data: any) => {
      var day = null;
      var dataSerie = [];
      var datum = null;
      var totalUsers = 0;
      var totalDays = 0;
      for (var i = 0, length = data.length; i < length; i++) {
        datum = data[i];
        day = new Date(datum["timeframe"]["start"]);
        if (!_isSatOrSun(day.getTime())) {
          dataSerie.push([
            new Date(
              Date.UTC(
                day.getUTCFullYear(),
                day.getUTCMonth(),
                day.getUTCDate()
              )
            ).getTime(),
            datum["value"],
          ]);

          totalDays = totalDays + 1;
          totalUsers = totalUsers + datum["value"];
        }
      }

      var series = [
        {
          id: type.join("-"),
          name: "Application daily users (avg)",
          animation: false,
          color: "#759dc0",
          connectNulls: true,
          data: dataSerie,
          dataGrouping: {
            units: [["day", [1]]],
          },
          marker: { enabled: false },
          states: {
            hover: {
              lineWidthPlus: 0,
            },
          },
          type: "spline",
        },
      ];

      var preparedData = {
        dailyUsers: totalDays ? Math.round(totalUsers / totalDays) : 0,
        series: series,
      };

      return preparedData;
    },
    [type]
  );
  useEffect(() => {
    keenIOAPI
      .cached()
      .then((response) => {
        const _series = _dataPrepare(response.data.result);
        setSeries(_series.series);
        setDailyUsers(_series.dailyUsers);
      });
  }, [_dataPrepare, keenIOAPI]);

  useEffect(() => {
    if (containerRef.current && chartRef.current) {
      const containerRect = containerRef.current.getBoundingClientRect();
      chartRef.current.setSize(undefined, containerRect.height);
    }
  });

  const chartConfig: Highcharts.Options = useMemo(
    () => ({
      chart: {
        zoomType: "x",
        type: "column",
        height: null,
        width: null,
      },
      credits: { enabled: false },
      exporting: { enabled: false },
      legend: { enabled: false },
      navigator: { enabled: false },
      rangeSelector: { enabled: false },
      series: series,
      scrollbar: { enabled: false },
      title: {
        align: "left",
        style: {
          fontFamily: "Open Sans",
          //   fontSize: ".9em",
        },
        text: "",
      },
      xAxis: {
        type: "datetime",
        minRange: 24 * 60 * 60 * 1000,
        endOnTick: false,
      },
      yAxis: {
        allowDecimals: false,
        endOnTick: false,
        min: 0,
        opposite: false,
        title: { text: null },
        startOnTick: false,
      },
    }),
    [series]
  );

  return series ? (
    <Box
      height={"100%"}
      width={"100%"}
      display={"flex"}
      flexDirection={"column"}
      gap={1}
    >
      <Box p={1} display={"flex"} alignItems={"center"} gap={1} boxShadow={3} borderRadius={1}>
        <Typography variant="h4">
          <strong>{dailyUsers} </strong>
        </Typography>
        <Typography>Application daily users (avg)</Typography>
      </Box>
      <Box height={"100%"} width={"100%"} minHeight={0} ref={containerRef}>
        <Chart ref={chartRef} options={chartConfig} />
      </Box>
    </Box>
  ) : (
    <CircularProgress color="secondary" />
  );
}

const _isSatOrSun = (milliseconds: any) => {
  var day = new Date(milliseconds);
  var dayOfWeek = day.getUTCDay();
  if (dayOfWeek === 0 || dayOfWeek === 6) {
    return true;
  }
  return false;
};
